import type { LoaderFunctionArgs } from 'react-router-dom';
import { redirect } from 'react-router-dom';
import { findRouteByPathname } from 'routing/matcher';
import {
  DEFAULT_LOCALE,
  LOCALE_SEGMENT_REGEX,
  applyLocaleToPath,
  isSupportedLocale,
  normalizeLocale,
  stripLocaleFromPath,
} from 'routing/paths';
import useAuthStore from '~/authentication/store';

const buildRedirectUrl = (url: URL, nextPathname: string) => {
  const next = new URL(url.toString());
  next.pathname = nextPathname;
  return `${next.pathname}${next.search}${next.hash}`;
};

const buildLoginRedirect = (
  url: URL,
  locale: string,
  returnTo: string,
): string => {
  const loginPath = applyLocaleToPath('/signup/', locale);
  const loginUrl = new URL(loginPath, url.origin);
  loginUrl.searchParams.set('returnTo', returnTo);
  loginUrl.searchParams.set('modal', '1');
  return `${loginUrl.pathname}${loginUrl.search}${loginUrl.hash}`;
};

export const localeLoader = ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const originalPathname = url.pathname;
  const originalSearch = url.search;
  const originalHash = url.hash;
  const originalFullPath = `${originalPathname}${originalSearch}${originalHash}`;

  // Enforce trailing slash for all paths (except files with extensions)
  if (!originalPathname.endsWith('/') && !originalPathname.includes('.')) {
    return redirect(`${originalPathname}/${originalSearch}${originalHash}`);
  }

  const match = originalPathname.match(LOCALE_SEGMENT_REGEX);
  const rawLocale = match?.[1];
  const lowerLocale = rawLocale?.toLowerCase();
  const normalizedLocale = normalizeLocale(lowerLocale);

  if (rawLocale) {
    if (!lowerLocale || !isSupportedLocale(lowerLocale)) {
      const fallbackPath = stripLocaleFromPath(originalPathname);
      if (fallbackPath !== originalPathname) {
        return redirect(buildRedirectUrl(url, fallbackPath));
      }
      return redirect('/');
    }

    if (normalizedLocale === DEFAULT_LOCALE) {
      const fallbackPath = stripLocaleFromPath(originalPathname);
      if (fallbackPath !== originalPathname) {
        return redirect(buildRedirectUrl(url, fallbackPath));
      }
      return null;
    }

    if (rawLocale !== lowerLocale) {
      const normalizedPath = applyLocaleToPath(
        stripLocaleFromPath(originalPathname),
        lowerLocale,
      );
      if (normalizedPath !== originalPathname) {
        return redirect(buildRedirectUrl(url, normalizedPath));
      }
    }
  }

  const canonicalPath = stripLocaleFromPath(originalPathname);
  const descriptor = findRouteByPathname(canonicalPath);

  if (descriptor?.audience === 'host') {
    const auth = useAuthStore.getState();
    if (!auth?._hasHydrated || auth.loading) {
      return null;
    }
    const isLoggedIn = auth.isLoggedIn?.() ?? false;
    if (!isLoggedIn) {
      const loginRedirect = buildLoginRedirect(
        url,
        normalizedLocale,
        originalFullPath,
      );
      return redirect(loginRedirect);
    }
  }

  return null;
};
